<template>
  <vx-card class="min-h-screen">
    <ResponseAlert ref="response" />

    <div v-if="loading" class="py-12" aria-busy="true" aria-label="Loading campaign settings">
      <vs-progress indeterminate color="primary" />
    </div>

    <form v-else @submit.prevent="save">
      <div
        v-for="platform in platforms"
        :key="platform.platformId"
        class="campaign-rule-section"
      >
        <h2 class="campaign-rule-title">{{ platform.platformName }}</h2>

        <div class="campaign-rule-header" aria-hidden="true">
          <span>Subplatform</span>
          <span>Minimum</span>
          <span>Maximum</span>
        </div>

        <div
          v-for="subPlatform in platform.subPlatforms"
          :key="subPlatform.subPlatformId"
          class="campaign-rule-row"
        >
          <label :for="`minimum-${subPlatform.subPlatformId}`">
            {{ subPlatform.subPlatformName }}
          </label>
          <input
            :id="`minimum-${subPlatform.subPlatformId}`"
            v-model.number="subPlatform.minimumLinks"
            type="number"
            min="1"
            step="1"
            class="campaign-rule-input"
            :aria-label="`Minimum link ${subPlatform.subPlatformName} ${platform.platformName}`"
            @keydown="preventInvalidNumberInput"
          />
          <input
            :id="`maximum-${subPlatform.subPlatformId}`"
            :value="subPlatform.maximumLinks"
            type="number"
            min="1"
            step="1"
            placeholder="Unlimited"
            class="campaign-rule-input"
            :aria-label="`Maximum link ${subPlatform.subPlatformName} ${platform.platformName}`"
            @input="setMaximum(subPlatform, $event.target.value)"
            @keydown="preventInvalidNumberInput"
          />
        </div>
      </div>

      <div v-if="!platforms.length" class="py-12 text-center text-grey" role="status">
        Campaign link rules tidak tersedia.
      </div>

      <div class="flex justify-end mt-6" v-if="platforms.length">
        <vs-button
          button="submit"
          color="primary"
          type="filled"
          icon="save"
          :disabled="saving"
        >
          {{ saving ? 'Saving' : 'Save' }}
        </vs-button>
      </div>
    </form>
  </vx-card>
</template>

<script>
import api from '@/utils/api'

export default {
  name: 'CampaignSetting',
  data() {
    return {
      loading: true,
      saving: false,
      platforms: []
    }
  },
  created() {
    this.getRules()
  },
  methods: {
    getRules() {
      this.loading = true
      return this.$axios.get(api.campaignLinkRules)
        .then(response => {
          this.platforms = response.data.data || []
        })
        .catch(error => {
          this.$refs.response.setResponse(error.response.data, 'danger')
        })
        .finally(() => {
          this.loading = false
        })
    },
    preventInvalidNumberInput(event) {
      if (['e', 'E', '+', '-', '.', ','].includes(event.key)) event.preventDefault()
    },
    setMaximum(subPlatform, value) {
      subPlatform.maximumLinks = value === '' ? null : Number(value)
    },
    getValidationError() {
      for (const platform of this.platforms) {
        for (const subPlatform of platform.subPlatforms) {
          const minimum = Number(subPlatform.minimumLinks)
          const maximum = subPlatform.maximumLinks === null
            ? null
            : Number(subPlatform.maximumLinks)
          const target = `${subPlatform.subPlatformName} ${platform.platformName}`

          if (!Number.isInteger(minimum) || minimum < 1) {
            return `Minimum link untuk ${target} harus berupa angka minimal 1.`
          }
          if (maximum !== null && (!Number.isInteger(maximum) || maximum < minimum)) {
            return `Maximum link untuk ${target} harus sama dengan atau lebih besar dari minimum.`
          }
        }
      }
      return null
    },
    save() {
      const validationError = this.getValidationError()
      if (validationError) {
        this.$refs.response.setResponseCustom(validationError, 'danger')
        window.scrollTo(0, 0)
        return
      }

      const rules = this.platforms.reduce((result, platform) =>
        result.concat(platform.subPlatforms.map(subPlatform => ({
          subPlatformId: subPlatform.subPlatformId,
          minimumLinks: Number(subPlatform.minimumLinks),
          maximumLinks: subPlatform.maximumLinks === null
            ? null
            : Number(subPlatform.maximumLinks)
        }))), [])

      this.saving = true
      this.$axios.put(api.campaignLinkRules, { rules })
        .then(response => {
          this.platforms = response.data.data || []
          this.$refs.response.setResponse(response.data, 'success')
        })
        .catch(error => {
          this.$refs.response.setResponse(error.response.data, 'danger')
        })
        .finally(() => {
          this.saving = false
        })
    }
  }
}
</script>

<style scoped>
.campaign-rule-section {
  border-bottom: 1px solid #e4e7eb;
  padding: 1.25rem 0;
}

.campaign-rule-title {
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
}

.campaign-rule-header,
.campaign-rule-row {
  align-items: center;
  display: grid;
  gap: 1rem;
  grid-template-columns: minmax(10rem, 1fr) minmax(7rem, 10rem) minmax(7rem, 10rem);
  min-height: 3rem;
}

.campaign-rule-header {
  color: #626b79;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.campaign-rule-row {
  border-top: 1px solid #f0f1f3;
}

.campaign-rule-input {
  border: 1px solid #d8dce2;
  border-radius: 4px;
  height: 2.25rem;
  padding: 0 0.625rem;
  width: 100%;
}

.campaign-rule-input:focus {
  border-color: #7367f0;
  outline: 2px solid rgba(115, 103, 240, 0.18);
}

@media (max-width: 640px) {
  .campaign-rule-header {
    display: none;
  }

  .campaign-rule-row {
    grid-template-columns: 1fr 1fr;
    padding: 0.75rem 0;
  }

  .campaign-rule-row label {
    grid-column: 1 / -1;
  }
}
</style>
